import { ChevronLeft } from '@app/components/various/Icons';
import { LightboxPagination } from '@app/components/various/Lightbox/LightboxPagination';
import { useLightboxKeyboardControls } from '@app/utils/hooks/useLightboxKeyboardControls';
import { isDefined } from '@app/utils/is';
import { AnyLightboxMedia } from '@typings/internal';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../Button';
import { FocusTrap } from '../FocusTrap';

import { Gallery } from './Gallery';

type LightboxProps = {
  medias: AnyLightboxMedia[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onChange: (index: number) => void;
};

export const Lightbox: React.FC<LightboxProps> = ({ medias, currentIndex, onClose, isOpen, onChange }) => {
  const { t } = useTranslation(['common']);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const handlePreviousMedia = React.useCallback(() => {
    onChange(currentIndex - 1);
  }, [onChange, currentIndex]);

  const handleNextMedia = React.useCallback(() => {
    onChange(currentIndex + 1);
  }, [onChange, currentIndex]);

  useLightboxKeyboardControls({ onClose, onNext: handleNextMedia, onPrevious: handlePreviousMedia });

  if (!isOpen) {
    return null;
  }

  return (
    <FocusTrap>
      <div className="fixed inset-0 z-overlay flex select-none items-center justify-center bg-white" ref={containerRef}>
        <div className="pointer-events-none absolute top-4 z-overlay flex w-screen justify-between px-4">
          <Button
            color="secondary"
            variant="bordered"
            onClick={onClose}
            className="pointer-events-auto bg-white"
            icon={ChevronLeft}
            size="medium"
          >
            {t('common:go_back')}
          </Button>
          {isDefined(medias[currentIndex]?.label) && (
            <div className="flex flex-1 items-center justify-end overflow-hidden lg:absolute lg:left-1/2 lg:-translate-x-1/2">
              <p className="max-w-none truncate rounded-regular bg-white px-3 py-1 text-center body-1 sm:max-w-60 lg:max-w-80">
                {medias[currentIndex]?.label}
              </p>
            </div>
          )}
        </div>
        <LightboxPagination onChange={onChange} currentIndex={currentIndex} lastIndex={medias.length - 1} />
        <Gallery currentIndex={currentIndex} setIndex={onChange} medias={medias} />
      </div>
    </FocusTrap>
  );
};
